import React from "react";
import { Navigate, Route, RouteProps } from "react-router-dom";

// components
import EpisodeChat from "../components/Chat/EpisodeChat";
import { UserRole } from "../constants/userRole";
import Story from "../pages/story/Story";
import StoryDetail from "../pages/story/StoryDetail";
import StoryGalleryDetail from "../components/StoryGallery/StoryGalleryDetail";
import PrivateRoute from "./PrivateRoute";
import LogErrorAi from "../pages/logErrorAi/LogErrorAi";
import LogErrorAiDetail from "../pages/logErrorAi/LogErrorAiDetail";
import StoryCustomization from "../pages/storyCustomization/StoryCustomization";
import StoryCustomizationChat from "../components/Chat/StoryCustomizationChat";
import EpisodeCaptureImage from "../pages/episodeCapturteImage/EpisodeCaptureImage";
import EpisodeCaptureVideo from "../pages/episodeCapturteVideo/EpisodeCaptureVideo";
import StoryCollection from "../pages/storyCollection/StoryCollection";
import StoryCollectionDetail from "../pages/storyCollection/StoryCollectionDetail";
// import Root from './Root';

// lazy load essential views

// auth
const Login = React.lazy(() => import("../pages/auth/Login"));
const Logout = React.lazy(() => import("../pages/auth/Logout"));
const Confirm = React.lazy(() => import("../pages/auth/Confirm"));
const ForgetPassword = React.lazy(() => import("../pages/auth/ForgetPassword"));
const Register = React.lazy(() => import("../pages/auth/Register"));
const SignInSignUp = React.lazy(() => import("../pages/auth/SignInSignUp"));
const LockScreen = React.lazy(() => import("../pages/auth/LockScreen"));

// auth2
const Login2 = React.lazy(() => import("../pages/auth2/Login2"));
const Logout2 = React.lazy(() => import("../pages/auth2/Logout2"));
const Register2 = React.lazy(() => import("../pages/auth2/Register2"));
const Confirm2 = React.lazy(() => import("../pages/auth2/Confirm2"));
const ForgetPassword2 = React.lazy(
  () => import("../pages/auth2/ForgetPassword2")
);
const LockScreen2 = React.lazy(() => import("../pages/auth2/LockScreen2"));
const SignInSignUp2 = React.lazy(() => import("../pages/auth2/SignInSignUp2"));

// user
const Users = React.lazy(() => import("../pages/users/Users"));
const BannedWord = React.lazy(() => import("../pages/bannedWord/BannedWord"));

// notification
const Notification = React.lazy(() => import("../pages/notification/Notification"));

// tag
const Tag = React.lazy(() => import("../pages/tag/Tag"));

export interface RoutesProps {
  path: RouteProps["path"];
  name?: string;
  element?: RouteProps["element"];
  route?: any;
  exact?: boolean;
  icon?: string;
  header?: string;
  roles?: string[];
  children?: RoutesProps[];
}

// users
const userAppRoutes: RoutesProps = {
  path: "/users",
  name: "Users",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "user",
  element: <Users />,
  header: "Apps",
};

const storyAppRoutes: RoutesProps = {
  path: "/stories",
  name: "Stories",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "file-text",
  element: <Story />,
  header: "Apps",
};

const collectionStoriesAppRoutes: RoutesProps = {
  path: "/collections/stories",
  name: "Collection Stories",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "file-text",
  element: <StoryCollection />,
  header: "Apps",
}


const collectionsAppRoutes: RoutesProps = {
  path: "/collections",
  name: "Collections",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "file-text",
  element: <StoryCollection />,
  header: "Apps",
}

const collectionDetailAppRoutes: RoutesProps = {
  path: "/collections/:collectionId",
  name: "Collection Detail",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "file-text",
  element: <StoryCollectionDetail />,
  header: "Apps",
}

const storyDetailAppRoutes: RoutesProps = {
  path: "/stories/:storyId",
  name: "Story detail",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "layers",
  element: <StoryDetail />,
  header: "Apps",
};

const episodeChatAppRoutes: RoutesProps = {
  path: "/stories/:storyId/episodes/:episodeId/chat",
  name: "Episode chat",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "layers",
  element: <EpisodeChat />,
  header: "Apps",
};

const storyGalleryAppRoutes: RoutesProps = {
  path: "/stories/:storyId/galleries/:galleryId",
  name: "Gallery",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "layers",
  element: <StoryGalleryDetail />,
  header: "Apps",
};

const notificationAppRoutes: RoutesProps = {
  path: "/notifications",
  name: "Notifications",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "bell",
  element: <Notification />,
  header: "Apps",
};

const tagAppRoutes: RoutesProps = {
  path: "/tags",
  name: "Tags",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "tag",
  element: <Tag />,
  header: "Apps",
}

const bannedWordAppRoutes: RoutesProps = {
  path: "/banned-words",
  name: "Banned words",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "slash",
  element: <BannedWord />,
  header: "Apps",
};

const logErrorAiAppRoutes: RoutesProps = {
  path: "/error_ai_logs",
  name: "Log error AI",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "alert-circle",
  element: <LogErrorAi/>,
  header: "Apps",
};

const logErrorAiDetailAppRoutes: RoutesProps = {
  path: "/error_ai_logs/:logErrorAiId",
  name: "Log error AI detail",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "alert-circle",
  element: <LogErrorAiDetail/>,
  header: "Apps",
}

const storyCustomizationAppRoutes: RoutesProps = {
  path: "/story_customizations",
  name: "Story Customizations",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "file-plus",
  element: <StoryCustomization/>,
  header: "Apps",
};

const storyCustomizationChatAppRoutes: RoutesProps = {
  path: "/story_customizations/:storyCustomizationId/chat",
  name: "Story Customization Chat",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "file-plus",
  element: <StoryCustomizationChat/>,
  header: "Apps",
};

const episodeCaptureImageAppRoutes: RoutesProps = {
  path: "/episode_capture_images",
  name: "Log error AI",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "camera",
  element: <EpisodeCaptureImage/>,
  header: "Apps",
};

const episodeCaptureVideoAppRoutes: RoutesProps = {
  path: "/episode_capture_videos",
  name: "Episode Capture Video",
  route: PrivateRoute,
  roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  icon: "camera",
  element: <EpisodeCaptureVideo/>,
  header: "Apps",
}


// auth
const authRoutes: RoutesProps[] = [
  {
    path: "/auth/login",
    name: "Login",
    element: <Login />,
    route: Route,
  },
  {
    path: "/auth/register",
    name: "Register",
    element: <Register />,
    route: Route,
  },
  {
    path: "/auth/confirm",
    name: "Confirm",
    element: <Confirm />,
    route: Route,
  },
  {
    path: "/auth/forget-password",
    name: "Forget Password",
    element: <ForgetPassword />,
    route: Route,
  },
  {
    path: "/auth/signin-signup",
    name: "SignIn-SignUp",
    element: <SignInSignUp />,
    route: Route,
  },
  {
    path: "/auth/lock-screen",
    name: "Lock Screen",
    element: <LockScreen />,
    route: Route,
  },
  {
    path: "/auth/logout",
    name: "Logout",
    element: <Logout />,
    route: Route,
  },
];

// auth2
const auth2Routes: RoutesProps[] = [
  {
    path: "/auth2/login",
    name: "Login2",
    element: <Login2 />,
    route: Route,
  },
  {
    path: "/auth2/register",
    name: "Register2",
    element: <Register2 />,
    route: Route,
  },
  {
    path: "/auth2/confirm",
    name: "Confirm2",
    element: <Confirm2 />,
    route: Route,
  },
  {
    path: "/auth2/forget-password",
    name: "Forget Password2",
    element: <ForgetPassword2 />,
    route: Route,
  },
  {
    path: "/auth2/signin-signup",
    name: "SignIn-SignUp2",
    element: <SignInSignUp2 />,
    route: Route,
  },
  {
    path: "/auth2/lock-screen",
    name: "Lock Screen2",
    element: <LockScreen2 />,
    route: Route,
  },
  {
    path: "/auth2/logout",
    name: "Logout2",
    element: <Logout2 />,
    route: Route,
  },
];

// public routes
const otherPublicRoutes = [
  {
    path: "*",
    name: "Error - 404",
    element: <Navigate to="/auth/login" />,
    route: Route,
  },
];

// flatten the list of all nested routes
const flattenRoutes = (routes: RoutesProps[]) => {
  let flatRoutes: RoutesProps[] = [];

  routes = routes || [];
  routes.forEach((item: RoutesProps) => {
    flatRoutes.push(item);

    if (typeof item.children !== "undefined") {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
    }
  });
  return flatRoutes;
};

// All routes
const allRoutes = [
  storyAppRoutes,
  storyDetailAppRoutes,
  collectionsAppRoutes,
  collectionStoriesAppRoutes,
  collectionDetailAppRoutes,
  episodeChatAppRoutes,
  storyGalleryAppRoutes,
  userAppRoutes,
  notificationAppRoutes,
  tagAppRoutes,
  bannedWordAppRoutes,
  logErrorAiAppRoutes,
  logErrorAiDetailAppRoutes,
  storyCustomizationAppRoutes,
  storyCustomizationChatAppRoutes,
  episodeCaptureImageAppRoutes,
  episodeCaptureVideoAppRoutes,
];

const authProtectedRoutes = [
  { path: "/", exact: true, element: <Navigate to="/stories" />, route: PrivateRoute },
  ...allRoutes,
];
const publicRoutes = [...authRoutes, ...auth2Routes, ...otherPublicRoutes];

const authProtectedFlattenRoutes = flattenRoutes([...authProtectedRoutes]);
const publicProtectedFlattenRoutes = flattenRoutes([...publicRoutes]);

export { authProtectedFlattenRoutes, publicProtectedFlattenRoutes };

